/**
 * Local durable media store (IndexedDB).
 * Same storageKey layout as future S3/R2/Supabase — swap adapter only.
 */

import type { MediaKind } from "../domain/types";
import type { MediaObjectMeta } from "./uploadAdapter";

const DB_NAME = "un8.media.v1";
const DB_VERSION = 1;
const STORE = "objects";

interface StoredObject {
  storageKey: string;
  blob: Blob;
  meta: MediaObjectMeta;
}

const urlCache = new Map<string, string>();

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "storageKey" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB open failed"));
  });
}

function idbReq<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB request failed"));
  });
}

export const localMediaDb = {
  async put(storageKey: string, blob: Blob, meta: MediaObjectMeta): Promise<void> {
    const db = await openDb();
    try {
      const tx = db.transaction(STORE, "readwrite");
      await idbReq(tx.objectStore(STORE).put({ storageKey, blob, meta } satisfies StoredObject));
    } finally {
      db.close();
    }
    // Keep a session object URL for fast preview
    const prev = urlCache.get(storageKey);
    if (prev?.startsWith("blob:")) URL.revokeObjectURL(prev);
    urlCache.set(storageKey, URL.createObjectURL(blob));
  },

  async get(storageKey: string): Promise<StoredObject | null> {
    const db = await openDb();
    try {
      const tx = db.transaction(STORE, "readonly");
      const row = await idbReq(tx.objectStore(STORE).get(storageKey));
      return (row as StoredObject | undefined) ?? null;
    } finally {
      db.close();
    }
  },

  async delete(storageKey: string): Promise<void> {
    const cached = urlCache.get(storageKey);
    if (cached?.startsWith("blob:")) URL.revokeObjectURL(cached);
    urlCache.delete(storageKey);
    const db = await openDb();
    try {
      const tx = db.transaction(STORE, "readwrite");
      await idbReq(tx.objectStore(STORE).delete(storageKey));
    } finally {
      db.close();
    }
  },

  async getBlobUrl(storageKey: string): Promise<string | null> {
    const cached = urlCache.get(storageKey);
    if (cached) return cached;
    const row = await this.get(storageKey);
    if (!row) return null;
    const url = URL.createObjectURL(row.blob);
    urlCache.set(storageKey, url);
    return url;
  },

  peekBlobUrl(storageKey: string): string | null {
    return urlCache.get(storageKey) ?? null;
  },
};

export function buildMeta(input: {
  storageKey: string;
  userId: string;
  assetId: string;
  kind: MediaKind;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
}): MediaObjectMeta {
  return {
    ...input,
    createdAt: new Date().toISOString(),
  };
}
